import { useState, useEffect } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { toast } from 'react-toastify';
import { 
  FaLink, 
  FaPlus, 
  FaTimes,
  FaSpinner
} from 'react-icons/fa';

import { 
  getAssetsForProjectItem, 
  addAssetToProjectItem 
} from '../api/projects';
import { getProjectAssets, ProjectAsset } from '../api/assets';

interface TaskAssetAttachmentProps {
  projectId: string;
  itemId: string;
  onUpdate?: () => void;
}

export const TaskAssetAttachment: React.FC<TaskAssetAttachmentProps> = ({
  projectId,
  itemId,
  onUpdate
}) => {
  const { keycloak } = useKeycloak();
  const [isLinking, setIsLinking] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedAssetId, setSelectedAssetId] = useState('');
  
  // Asset data state
  const [linkedAssets, setLinkedAssets] = useState<ProjectAsset[]>([]);
  const [availableAssets, setAvailableAssets] = useState<ProjectAsset[]>([]);

  // Load linked assets for this item
  const loadLinkedAssets = async () => {
    if (!keycloak.token || !projectId || !itemId) return;

    try {
      console.log('Loading linked assets for item:', itemId);
      const assets = await getAssetsForProjectItem(keycloak.token, projectId, itemId);
      console.log('Received assets:', assets);
      setLinkedAssets(assets);
    } catch (error) {
      console.error('Error loading linked assets:', error);
      toast.error('Failed to load linked assets');
    }
  };

  // Load available assets for linking
  const loadAvailableAssets = async () => {
    if (!keycloak.token) return;

    try {
      const assetsResponse = await getProjectAssets(keycloak.token, projectId);
      // Filter out already linked assets
      const available = assetsResponse.results.filter((asset: ProjectAsset) => 
        !linkedAssets.some(linked => linked.id === asset.id)
      );
      setAvailableAssets(available);
    } catch (error) {
      console.error('Error loading available assets:', error);
      toast.error('Failed to load available assets');
    }
  };

  // Handle asset selection
  const handleAssetSelect = (assetId: string) => {
    setSelectedAssetId(assetId);
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedAssetId) {
      toast.error('Please select an asset to link');
      return;
    }

    setIsLinking(true);
    try {
      await addAssetToProjectItem(keycloak.token || '', projectId, itemId, selectedAssetId);
      toast.success('Asset linked successfully');
      
      // Reload linked assets
      await loadLinkedAssets();
      
      // Reset form
      setSelectedAssetId('');
      setShowModal(false);
      
      // Trigger parent update
      if (onUpdate) {
        await onUpdate();
      }
    } catch (error: any) {
      console.error('Error linking asset:', error);
      
      if (error.message && error.message.includes('already linked')) {
        toast.warning('Asset is already linked to this item');
      } else {
        toast.error('Failed to link asset');
      }
    } finally {
      setIsLinking(false);
    }
  };

  // Load data when component mounts or props change
  useEffect(() => {
    loadLinkedAssets();
  }, [projectId, itemId]);

  useEffect(() => {
    if (showModal) {
      loadAvailableAssets();
    }
  }, [showModal, linkedAssets]);

  const content = (
    <div className="bg-white">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-gray-200">
        <div className="flex items-center space-x-3">
          <FaLink className="w-5 h-5 text-blue-500" />
          <h2 className="text-xl font-semibold font-['Arcadia_Display',system-ui,sans-serif]">
            Link Assets
          </h2>
        </div>
        <button
          onClick={() => setShowModal(false)}
          className="p-2 hover:bg-gray-100 rounded-md transition-colors"
          disabled={isLinking}
        >
          <FaTimes className="w-4 h-4 text-gray-500" />
        </button>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Asset Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-4 font-['Arcadia_Text',system-ui,sans-serif]">
            Select Asset *
          </label>
          {availableAssets.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FaLink className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No available assets to link</p>
              <p className="text-sm">All assets are already linked to this item</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {availableAssets.map((asset) => (
                <div key={asset.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors">
                  <div className="flex-1">
                    <div className="font-medium text-sm text-gray-900 font-['Arcadia_Text',system-ui,sans-serif]">{asset.name}</div>
                    {asset.description && (
                      <div className="text-xs text-gray-500 font-['Arcadia_Text',system-ui,sans-serif]">{asset.description}</div>
                    )}
                  </div>
                  <input
                    type="radio"
                    name="selectedAssetId"
                    value={asset.id}
                    checked={selectedAssetId === asset.id}
                    onChange={(e) => handleAssetSelect(e.target.value)}
                    className="ml-3"
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Submit Button */}
        <div className="flex space-x-3 pt-6">
          <button
            type="button"
            onClick={() => setShowModal(false)}
            className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 font-['Arcadia_Text',system-ui,sans-serif]"
            disabled={isLinking}
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            className="flex-1 px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 font-['Arcadia_Text',system-ui,sans-serif] disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isLinking || !selectedAssetId}
          >
            {isLinking ? (
              <div className="flex items-center justify-center">
                <FaSpinner className="w-4 h-4 animate-spin mr-2" />
                Linking...
              </div>
            ) : (
              'Link Asset'
            )}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Button */}
      {linkedAssets.length > 0 ? (
        // Assets exist - show count and actions
        <button
          onClick={() => setShowModal(true)}
          className="cursor-pointer flex items-center space-x-1 text-xs text-gray-600 hover:text-gray-800 bg-gray-50 border border-gray-200 rounded-lg px-2 py-1"
          title={`${linkedAssets.length} linked asset${linkedAssets.length !== 1 ? 's' : ''}`}
        >
          <FaLink className="text-xs" />
          <span className="font-medium">
            {linkedAssets.length}
          </span>
        </button>
      ) : (
        // No assets - show add button
        <button
          onClick={() => setShowModal(true)}
          disabled={isLinking}
          className="flex items-center space-x-1 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg px-3 py-1 text-xs text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          title="Link assets"
        >
          {isLinking ? (
            <>
              <div className="animate-spin w-3 h-3 border border-gray-400 border-t-gray-700 rounded-full"></div>
              <span>Linking...</span>
            </>
          ) : (
            <>
              <FaPlus className="text-xs" />
              <span>Link</span>
            </>
          )}
        </button>
      )}

      {/* Modal - Exactly like PhaseManager */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
            {content}
          </div>
        </div>
      )}
    </>
  );
}; 